import React, { useState, useEffect } from 'react';
import MainLayout from '../layout/MainLayout';
import {
  FileText, Download, LayoutGrid, Video, Target, TrendingUp,
  Users, GraduationCap, UserCheck, Handshake, MessageSquare, Star, CalendarCheck,
} from 'lucide-react';
import api from '../../services/api';
import './admin-reports.scss';

interface AnalyticsData {
  overall_stats: {
    total_users: number;
    total_mentors: number;
    total_mentees: number;
    active_relationships: number;
    total_sessions: number;
    completed_sessions: number;
    total_goals: number;
    completed_goals: number;
    total_messages: number;
  };
  sessions_by_type: Array<{ session_type: string; count: string }>;
  sessions_by_status: Array<{ status: string; count: string }>;
  goals_by_category: Array<{ category: string; count: string }>;
  goals_by_status: Array<{ status: string; count: string }>;
  relationships_by_status: Array<{ status: string; count: string }>;
  average_session_rating: { avg_rating: string; total_rated: string };
  sessions_timeline: Array<{ date: string; count: string }>;
  goals_progress_distribution: Array<{ progress_range: string; count: string }>;
  top_mentors: Array<{
    id: string;
    first_name: string;
    last_name: string;
    total_sessions: string;
    avg_rating: string;
  }>;
  engagement_metrics: {
    avg_sessions_per_relationship: number;
    avg_messages_per_day: number;
    avg_goal_progress: number;
  };
  monthly_trends: Array<{
    month: string;
    sessions_count: string;
    avg_rating: string;
  }>;
}

const AdminReports: React.FC = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<'overview' | 'sessions' | 'goals' | 'engagement'>('overview');

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await api.admin.getAnalytics();
      setAnalytics(response.data.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): string => {
    const colors: { [key: string]: string } = {
      completed: '#28a745',
      active: '#007bff',
      'in-progress': '#ffc107',
      pending: '#6c757d',
      scheduled: '#17a2b8',
      cancelled: '#dc3545',
    };
    return colors[status] || '#6c757d';
  };

  const safeToFixed = (value: any, decimals: number = 1): string => {
    if (value === null || value === undefined || isNaN(Number(value))) {
      return '0';
    }
    return Number(value).toFixed(decimals);
  };

  const getCategoryColor = (category: string): string => {
    const colors: { [key: string]: string } = {
      professional: '#007bff',
      technical: '#28a745',
      leadership: '#ffc107',
      communication: '#17a2b8',
      other: '#6c757d',
    };
    return colors[category] || '#6c757d';
  };

  const getTypeColor = (type: string): string => {
    const colors: { [key: string]: string } = {
      video_call: '#007bff',
      phone_call: '#28a745',
      in_person: '#ffc107',
      text_chat: '#17a2b8',
    };
    return colors[type] || '#6c757d';
  };

  // ===== Exportación =====
  const csvEscape = (v: any) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const exportExcel = () => {
    if (!analytics) return;
    const s = analytics.overall_stats;
    const rows: (string | number)[][] = [['Sección', 'Métrica', 'Valor']];
    rows.push(['Resumen', 'Usuarios totales', s.total_users]);
    rows.push(['Resumen', 'Mentores', s.total_mentors]);
    rows.push(['Resumen', 'Mentees', s.total_mentees]);
    rows.push(['Resumen', 'Duplas activas', s.active_relationships]);
    rows.push(['Resumen', 'Sesiones totales', s.total_sessions]);
    rows.push(['Resumen', 'Sesiones completadas', s.completed_sessions]);
    rows.push(['Resumen', 'Objetivos totales', s.total_goals]);
    rows.push(['Resumen', 'Objetivos completados', s.completed_goals]);
    rows.push(['Resumen', 'Mensajes totales', s.total_messages]);
    rows.push(['Calificación', 'Promedio', safeToFixed(analytics.average_session_rating?.avg_rating)]);
    rows.push(['Calificación', 'Total calificadas', analytics.average_session_rating?.total_rated ?? 0]);
    analytics.sessions_by_type?.forEach(r => rows.push(['Sesiones por tipo', r.session_type, r.count]));
    analytics.sessions_by_status?.forEach(r => rows.push(['Sesiones por estado', r.status, r.count]));
    analytics.goals_by_category?.forEach(r => rows.push(['Objetivos por categoría', r.category, r.count]));
    analytics.goals_by_status?.forEach(r => rows.push(['Objetivos por estado', r.status, r.count]));
    analytics.relationships_by_status?.forEach(r => rows.push(['Duplas por estado', r.status, r.count]));
    analytics.top_mentors?.forEach(m => rows.push(['Top mentores', `${m.first_name} ${m.last_name}`, `${m.total_sessions} sesiones · ${safeToFixed(m.avg_rating)}★`]));
    if (analytics.engagement_metrics) {
      rows.push(['Engagement', 'Sesiones por dupla', safeToFixed(analytics.engagement_metrics.avg_sessions_per_relationship)]);
      rows.push(['Engagement', 'Mensajes por día', safeToFixed(analytics.engagement_metrics.avg_messages_per_day)]);
      rows.push(['Engagement', 'Progreso promedio de objetivos', safeToFixed(analytics.engagement_metrics.avg_goal_progress)]);
    }
    analytics.monthly_trends?.forEach(m => rows.push(['Tendencia mensual', m.month, `${m.sessions_count} sesiones · ${safeToFixed(m.avg_rating)}★`]));

    const csv = '﻿' + rows.map(r => r.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reporte-mitsui-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => window.print();

  if (loading) {
    return (
      <MainLayout>
        <div className="admin-reports">
          <div className="loading-state">Cargando reportes y analytics...</div>
        </div>
      </MainLayout>
    );
  }

  if (!analytics) {
    return (
      <MainLayout>
        <div className="admin-reports">
          <div className="error-state">Error al cargar los reportes</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="admin-reports">
        {/* Page Header */}
        <div className="page-header">
          <div className="page-header__info">
            <div className="eyebrow">{(() => { const d = new Date(); return `${d.toLocaleDateString('es', { weekday: 'long' })} ${d.getDate()} de ${d.toLocaleDateString('es', { month: 'short' }).replace('.', '')} · Administrador`.toUpperCase(); })()}</div>
            <h1 className="heading-1">Reportes y <em>métricas</em></h1>
            <p className="page-meta">
              Dashboard completo de analíticas del programa de mentoría.
            </p>
          </div>
          <div className="page-header__actions">
            <button className="btn btn--secondary" onClick={exportPDF}>
              <FileText size={16} /> Exportar PDF
            </button>
            <button className="btn btn--primary" onClick={exportExcel}>
              <Download size={16} /> Exportar Excel
            </button>
          </div>
        </div>

        {/* Report Tabs */}
        <div className="report-tabs">
          <button
            className={`tab ${selectedReport === 'overview' ? 'active' : ''}`}
            onClick={() => setSelectedReport('overview')}
          >
            <LayoutGrid size={16} /> Resumen General
          </button>
          <button
            className={`tab ${selectedReport === 'sessions' ? 'active' : ''}`}
            onClick={() => setSelectedReport('sessions')}
          >
            <Video size={16} /> Sesiones
          </button>
          <button
            className={`tab ${selectedReport === 'goals' ? 'active' : ''}`}
            onClick={() => setSelectedReport('goals')}
          >
            <Target size={16} /> Objetivos
          </button>
          <button
            className={`tab ${selectedReport === 'engagement' ? 'active' : ''}`}
            onClick={() => setSelectedReport('engagement')}
          >
            <TrendingUp size={16} /> Engagement
          </button>
        </div>

        {/* Overview Report */}
        {selectedReport === 'overview' && (
          <div className="report-section">
            <h2 className="section-heading">Estadísticas Generales</h2>

            <div className="stats-grid">
              <div className="stat-card stat-card--large">
                <span className="stat-icon"><Users size={22} /></span>
                <div className="stat-info">
                  <span className="stat-value">{analytics.overall_stats.total_users}</span>
                  <span className="stat-label">Usuarios Totales</span>
                </div>
              </div>
              <div className="stat-card stat-card--large stat-card--primary">
                <span className="stat-icon"><GraduationCap size={22} /></span>
                <div className="stat-info">
                  <span className="stat-value">{analytics.overall_stats.total_mentors}</span>
                  <span className="stat-label">Mentores</span>
                </div>
              </div>
              <div className="stat-card stat-card--large stat-card--success">
                <span className="stat-icon"><UserCheck size={22} /></span>
                <div className="stat-info">
                  <span className="stat-value">{analytics.overall_stats.total_mentees}</span>
                  <span className="stat-label">Mentees</span>
                </div>
              </div>
              <div className="stat-card stat-card--large stat-card--info">
                <span className="stat-icon"><Handshake size={22} /></span>
                <div className="stat-info">
                  <span className="stat-value">{analytics.overall_stats.active_relationships}</span>
                  <span className="stat-label">Relaciones Activas</span>
                </div>
              </div>
              <div className="stat-card stat-card--large">
                <span className="stat-icon"><Video size={22} /></span>
                <div className="stat-info">
                  <span className="stat-value">{analytics.overall_stats.completed_sessions}</span>
                  <span className="stat-label">Sesiones Completadas</span>
                  <span className="stat-meta">de {analytics.overall_stats.total_sessions} totales</span>
                </div>
              </div>
              <div className="stat-card stat-card--large">
                <span className="stat-icon"><Target size={22} /></span>
                <div className="stat-info">
                  <span className="stat-value">{analytics.overall_stats.completed_goals}</span>
                  <span className="stat-label">Objetivos Cumplidos</span>
                  <span className="stat-meta">de {analytics.overall_stats.total_goals} totales</span>
                </div>
              </div>
              <div className="stat-card stat-card--large">
                <span className="stat-icon"><MessageSquare size={22} /></span>
                <div className="stat-info">
                  <span className="stat-value">{analytics.overall_stats.total_messages}</span>
                  <span className="stat-label">Mensajes Enviados</span>
                </div>
              </div>
              <div className="stat-card stat-card--large stat-card--warning">
                <span className="stat-icon"><Star size={22} /></span>
                <div className="stat-info">
                  <span className="stat-value">
                    {safeToFixed(analytics.average_session_rating.avg_rating, 1)}
                  </span>
                  <span className="stat-label">Rating Promedio</span>
                  <span className="stat-meta">
                    de {analytics.average_session_rating.total_rated} evaluaciones
                  </span>
                </div>
              </div>
            </div>

            {/* Top Mentors */}
            <h2 className="section-heading" style={{ marginTop: '40px' }}>Top Mentores</h2>
            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    <th>Mentor</th>
                    <th>Sesiones Totales</th>
                    <th>Rating Promedio</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.top_mentors.map((mentor) => (
                    <tr key={mentor.id}>
                      <td>
                        <strong>{mentor.first_name} {mentor.last_name}</strong>
                      </td>
                      <td>{mentor.total_sessions}</td>
                      <td>
                        <span className="rating-badge">
                          <Star size={13} fill="currentColor" /> {safeToFixed(mentor.avg_rating, 1)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Relationships by Status */}
            <h2 className="section-heading" style={{ marginTop: '40px' }}>Relaciones por Estado</h2>
            <div className="chart-container">
              <div className="bar-chart">
                {analytics.relationships_by_status.map((item) => {
                  const maxCount = Math.max(
                    ...analytics.relationships_by_status.map((i) => parseInt(i.count))
                  );
                  const percentage = (parseInt(item.count) / maxCount) * 100;
                  return (
                    <div key={item.status} className="bar-item">
                      <div className="bar-label">{item.status}</div>
                      <div className="bar-wrapper">
                        <div
                          className="bar-fill"
                          style={{
                            width: `${percentage}%`,
                            backgroundColor: getStatusColor(item.status),
                          }}
                        />
                      </div>
                      <div className="bar-value">{item.count}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Sessions Report */}
        {selectedReport === 'sessions' && (
          <div className="report-section">
            <h2 className="section-heading">Análisis de Sesiones</h2>

            {/* Sessions by Type */}
            <div className="chart-row">
              <div className="chart-card">
                <h3 className="chart-title">Sesiones por Tipo</h3>
                <div className="pie-chart-grid">
                  {analytics.sessions_by_type.map((item) => (
                    <div key={item.session_type} className="pie-item">
                      <div
                        className="pie-color"
                        style={{ backgroundColor: getTypeColor(item.session_type) }}
                      />
                      <div className="pie-info">
                        <span className="pie-label">{item.session_type}</span>
                        <span className="pie-value">{item.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sessions by Status */}
              <div className="chart-card">
                <h3 className="chart-title">Sesiones por Estado</h3>
                <div className="pie-chart-grid">
                  {analytics.sessions_by_status.map((item) => (
                    <div key={item.status} className="pie-item">
                      <div
                        className="pie-color"
                        style={{ backgroundColor: getStatusColor(item.status) }}
                      />
                      <div className="pie-info">
                        <span className="pie-label">{item.status}</span>
                        <span className="pie-value">{item.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Monthly Trends */}
            <h3 className="section-heading" style={{ marginTop: '40px' }}>Tendencias Mensuales</h3>
            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    <th>Mes</th>
                    <th>Sesiones</th>
                    <th>Rating Promedio</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.monthly_trends.map((trend) => (
                    <tr key={trend.month}>
                      <td><strong>{trend.month}</strong></td>
                      <td>{trend.sessions_count}</td>
                      <td>
                        <span className="rating-badge">
                          <Star size={13} fill="currentColor" /> {safeToFixed(trend.avg_rating, 1)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Goals Report */}
        {selectedReport === 'goals' && (
          <div className="report-section">
            <h2 className="section-heading">Análisis de Objetivos</h2>

            {/* Goals by Category and Status */}
            <div className="chart-row">
              <div className="chart-card">
                <h3 className="chart-title">Objetivos por Categoría</h3>
                <div className="pie-chart-grid">
                  {analytics.goals_by_category.map((item) => (
                    <div key={item.category} className="pie-item">
                      <div
                        className="pie-color"
                        style={{ backgroundColor: getCategoryColor(item.category) }}
                      />
                      <div className="pie-info">
                        <span className="pie-label">{item.category}</span>
                        <span className="pie-value">{item.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="chart-card">
                <h3 className="chart-title">Objetivos por Estado</h3>
                <div className="pie-chart-grid">
                  {analytics.goals_by_status.map((item) => (
                    <div key={item.status} className="pie-item">
                      <div
                        className="pie-color"
                        style={{ backgroundColor: getStatusColor(item.status) }}
                      />
                      <div className="pie-info">
                        <span className="pie-label">{item.status}</span>
                        <span className="pie-value">{item.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Goals Progress Distribution */}
            <h3 className="section-heading" style={{ marginTop: '40px' }}>Distribución de Progreso</h3>
            <div className="chart-container">
              <div className="bar-chart">
                {analytics.goals_progress_distribution.map((item) => {
                  const maxCount = Math.max(
                    ...analytics.goals_progress_distribution.map((i) => parseInt(i.count))
                  );
                  const percentage = (parseInt(item.count) / maxCount) * 100;
                  return (
                    <div key={item.progress_range} className="bar-item">
                      <div className="bar-label">{item.progress_range}</div>
                      <div className="bar-wrapper">
                        <div
                          className="bar-fill"
                          style={{
                            width: `${percentage}%`,
                            backgroundColor: '#007bff',
                          }}
                        />
                      </div>
                      <div className="bar-value">{item.count}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Engagement Report */}
        {selectedReport === 'engagement' && (
          <div className="report-section">
            <h2 className="section-heading">Métricas de Engagement</h2>

            <div className="stats-grid">
              <div className="stat-card stat-card--large stat-card--primary">
                <span className="stat-icon"><CalendarCheck size={22} /></span>
                <div className="stat-info">
                  <span className="stat-value">
                    {safeToFixed(analytics.engagement_metrics.avg_sessions_per_relationship, 1)}
                  </span>
                  <span className="stat-label">Sesiones Promedio</span>
                  <span className="stat-meta">por relación de mentoría</span>
                </div>
              </div>
              <div className="stat-card stat-card--large stat-card--success">
                <span className="stat-icon"><MessageSquare size={22} /></span>
                <div className="stat-info">
                  <span className="stat-value">
                    {safeToFixed(analytics.engagement_metrics.avg_messages_per_day, 1)}
                  </span>
                  <span className="stat-label">Mensajes por Día</span>
                  <span className="stat-meta">promedio últimos 30 días</span>
                </div>
              </div>
              <div className="stat-card stat-card--large stat-card--warning">
                <span className="stat-icon"><Target size={22} /></span>
                <div className="stat-info">
                  <span className="stat-value">
                    {safeToFixed(analytics.engagement_metrics.avg_goal_progress, 0)}%
                  </span>
                  <span className="stat-label">Progreso Promedio</span>
                  <span className="stat-meta">de objetivos del programa</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default AdminReports;
